import { MAX_UPLOADED_FILES_SIZE, PAYMENT_METHOD, PAYMENT_PROVIDERS } from 'components/common/constants';
import { FieldError } from 'services/Error';

export const emailRegExp =
  // eslint-disable-next-line max-len
  /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

export const passwordRegExp =
  // eslint-disable-next-line no-useless-escape
  /^(?=.*[A-Z])(?=.*[a-z])(?=.*\d)(?=.*[!\"#$%&'()*+,-.:;<=>?@\[\]^_`{/|\\}~])[\x20-\x7E]{7,30}$/;

export const registrationPasswordRegExp =
  // eslint-disable-next-line no-useless-escape
  /[^\x20-\x7E]/;

// eslint-disable-next-line no-useless-escape
export const passwordSpecialSymbolsRegExp = /[!\"#$%&'()*+,-.:;<=>?@\[\]^_`{/|\\}~]/;

// Regex that checks for absence of Cyrillic characters
// Allows everything EXCEPT Cyrillic letters (both uppercase and lowercase)
export const messageRegExp = /^[^\u0400-\u04FF\u0500-\u052F]*$/;

export const noNumbersRegExp = /^[^\d]*$/;

// Regex for validating recipient address in SWIFT payments for restricted providers
// Prohibits: & * _ % ! ? ; $ € £ « » „ " @ № [ ] { } < > \ ^ ` ~ | and diacritical characters (ä, ö, ü, é, ç, ñ, etc.)
// Allows: letters A-Z a-z, digits 0-9, space, and symbols / - : ( ) . , ' + =
export const SWIFT_RESTRICTED_PROVIDERS_ADDRESS_REG_EXP = /^[a-zA-Z0-9/\-:().,'+= ]*$/;

export const replaceSpaces = (s) => s?.replace(/\s/g, '');

// Whether an input may grab focus on page load. On a touch device (coarse primary pointer) that would also pop up
// the on-screen keyboard over half the screen, so there the user focuses the field with a tap instead.
export const shouldAutoFocus = () => !window.matchMedia?.('(pointer: coarse)')?.matches;

export const deleteNonLatin = (text) => text.replace(/[^A-Za-z0-9_ ]/gi, '');

export const formatSortCode = (string) => {
  const numericString = string?.replace(/\D/g, '')?.slice(0, 6);

  return numericString?.replace(/^(\d{2})(\d{2})(\d{2})$/, '$1-$2-$3');
};

export const convertBytesToMegabytes = (bytes) => bytes / (1024 * 1024);

export const strToFloat = (str) => {
  if (!str) return NaN;

  // Remove commas which are used as thousands separators
  const formattedStr = str.replace(/,/g, '');

  const trimmed = formattedStr.trim().replace(/\.$/, '');
  const float = parseFloat(trimmed);

  return float === Number(trimmed) ? float : Number.NaN;
};

export const getErrorMessageForAlert = (
  i18n,
  error,
  isIgnored = true,
  paymentMethod = null,
  serverFieldsErrors = null
) => {
  let translation = '';
  if (!error || (error instanceof FieldError && isIgnored)) return translation;

  const getSafeTranslation = (key, params) => {
    const translation = i18n.getMessage(key, params);
    // If the translation matches the key, it means there is no translation
    return translation === key ? i18n.getMessage('error.UNKNOWN_ERROR') : translation;
  };

  const translationId = `error.${error?.code}`;
  let firstFieldError = '';

  if (serverFieldsErrors) {
    const firstKey = Object.keys(serverFieldsErrors)[0];
    firstFieldError = serverFieldsErrors[firstKey];
  }

  // Handle OAuth2 specific error codes with their messages
  if (error?.code === 'BAD_REQUEST') {
    translation = error.message || 'Missing username or password';
  } else if (error?.code === 'INVALID_CREDENTIALS') {
    translation = error.message || 'Wrong username or password.';
  } else if (error?.code === 'TOO_MANY_REQUESTS') {
    translation = error.message || 'Too many requests. User is temporarily locked.';
  } else if (error?.code === 'CONFIRMATION_OUT_OF_DATE') {
    translation = error.message || 'Confirmation failed.';
  } else if (error?.code === 'CONFIRMATION_CODE_TRIES_EXCEED') {
    translation = error.message || 'Confirmation failed.';
  } else if (error?.code === 'CONFIRMATION_CODE_NOT_VALID') {
    translation = error.message || 'Confirmation failed.';
  } else if (error?.code === 'CONFIRMATION_PENDING_ACTION') {
    translation = '';
  } else if (error?.code === 'CONFIRMATION_REJECTED') {
    translation = error.message || 'Confirmation is rejected.';
  } else if (error?.code === 'CONFIRMATION_COMPLETED') {
    translation = error.message || 'Confirmation is completed.';
  } else if (error?.code === 'CONFIRMATION_STATUS_NOT_SUPPORTED') {
    translation = error.message || 'Confirmation status not supported.';
  } else if (error?.code === 'MESSAGE_NOT_READABLE') {
    translation = error.message || 'Message Not Readable';
  } else if (error?.code === 'ACCESS_DENIED') {
    translation = error.message || 'Access Denied';
  } else if (error?.code === 'ACCOUNT_REPORT_TOO_LARGE') {
    translation =
      error.message || 'Account report contains too many transactions. Narrow the date range or wallet selection.';
  } else if (error?.code === 'REQUEST_HAS_BEEN_TERMINATED') {
    translation = getSafeTranslation(translationId, { maxFilesSize: convertBytesToMegabytes(MAX_UPLOADED_FILES_SIZE) });
  } else if (error?.code === 'AMOUNT_CHECK_FAILED' || error?.message === 'Provider amount exceed.') {
    if (paymentMethod && paymentMethod === PAYMENT_METHOD.SEPA_INSTANT) {
      translation = getSafeTranslation('error.AMOUNT_CHECK_FAILED.SEPA_INSTANT');
    } else if (paymentMethod && paymentMethod === PAYMENT_METHOD.FASTER_PAYMENTS) {
      translation = getSafeTranslation('error.AMOUNT_CHECK_FAILED.FASTER_PAYMENTS');
    } else {
      translation = getSafeTranslation('error.AMOUNT_CHECK_FAILED');
    }
  } else if (error?.code === 'NOT_FOUND') {
    if (error?.message.includes('Exchange rate pair')) {
      const match = error?.message.match(/Exchange rate pair ([A-Z0-9_]+)\/([A-Z0-9_]+) not found/);
      const currencyPair = match ? `${match[1]}/${match[2]}` : 'UNKNOWN/UNKNOWN';
      translation = getSafeTranslation('error.CURRENCY_PAIR_NOT_FOUND', { currencyPair });
    } else {
      translation = error.message;
    }
  } else if (error?.code === 'ARGUMENT_NOT_VALID') {
    translation = serverFieldsErrors ? firstFieldError : error.message;
  } else if (serverFieldsErrors && serverFieldsErrors?.iban) {
    translation = firstFieldError || error.message;
  } else if (error?.code === 'IBAN_CHECK_FAILED') {
    translation = error.message;
  } else if (error?.code === 'INVALID_IBAN') {
    translation = error.message;
  } else if (error?.code === 'CRYPTO_ADDRESS_CHECK_FAILED') {
    translation = error.message;
  } else if (error?.code === 'AMOUNT_EXCEED') {
    translation = error.message;
  } else if (error?.code === 'ALREADY_LINKED') {
    translation = error.message;
  } else if (error?.code === 'PAYYE_CHECK_FAILED') {
    translation = error.message;
  } else if (error?.code === 'PHONE_SEND_MESSAGE_ERROR') {
    translation = error.message;
  } else if (error?.code === 'OPERATION_NOT_SUPPORTED') {
    translation = error.message;
  } else if (error?.code === 'WALLET_NETWORK_NOT_SUPPORTED') {
    translation = error.message || getSafeTranslation('error.crossNetworkCryptoInternalTransfer');
  } else if (error?.code === 'ILLEGAL_ARGUMENT') {
    translation = error.message;
  } else if (error?.code === 'PASSWORD_ALREADY_IN_USE') {
    translation = error.message;
  } else if (error?.code === 'DOCUMENT_FORMAT_UNSUPPORTED') {
    translation = error.message;
  } else if (error?.code === 'CODE_SEND_TIMEOUT') {
    translation = getSafeTranslation('error.CODE_SEND_TIMEOUT');
  } else if (error?.code === 'PAYMENT_CREATION_TIME_EXPIRED') {
    translation = getSafeTranslation('error.PAYMENT_CREATION_TIME_EXPIRED');
  } else if (error?.code === 'DOCUMENT_PROTECTED_WITH_PASSWORD') {
    translation = getSafeTranslation('error.DOCUMENT_PROTECTED_WITH_PASSWORD');
  } else if (error?.code === 'RECIPIENT_DATA_MISMATCH') {
    translation = getSafeTranslation('error.RECIPIENT_DATA_MISMATCH');
  } else if (error?.code === 'CAPTCHA_CHECK_FAILED') {
    translation = getSafeTranslation('error.CAPTCHA_CHECK_FAILED');
  } else if (error?.code === 'UNKNOWN_ERROR' || error?.message === 'Server Error') {
    translation = getSafeTranslation('error.SYSTEM_ERROR');
  } else if (error?.status === 504) {
    translation = getSafeTranslation('error.GETAWAY_TIME_OUT');
  } else if (error?.message === 'Unsuccessful HTTP response') {
    translation = getSafeTranslation('error.UNSUCCESSFUL_HTTP_RESPONSE');
  } else {
    translation = getSafeTranslation(translationId);
  }

  if (translationId === translation) {
    return error?.message || error?.code;
  } else {
    return translation;
  }
};

// Backend field-error keys are built from the raw (often long, dotted, backend-bean-shaped) field
// path plus a constraint name, e.g. `error.fieldError.transactionRecipient.requisites.bic.NoCyrillic`.
// Whenever the specific combination hasn't been translated yet, i18n.getMessage returns the lookup
// key itself verbatim — never surface that raw key to the user (under a field or in the general
// Alert, both of which read from this same result), fall back to a generic message instead.
const resolveFieldErrorMessage = (i18n, key, params) => {
  const message = i18n.getMessage(key, params);
  return message === key ? i18n.getMessage('error.field.code.WrongValue') : message;
};

export const prepareFieldErrors = (i18n, error, variable, isRegistration) => {
  let fieldsError = {};

  if (error?.details && Array.isArray(error?.details)) {
    error.details.forEach((detail) => {
      const fieldName = detail?.field.split('.').pop();

      if (detail?.code) {
        fieldsError[fieldName] = resolveFieldErrorMessage(i18n, `error.fieldError.${detail?.field}.${detail?.code}`, {
          variable
        });
      } else {
        fieldsError[fieldName] = detail?.message;
      }
    });
  }

  if (
    error?.fields &&
    error?.fields[0]?.code &&
    (error?.fields[0]?.code === 'NO_PAYMENT_METHODS_AVAILABLE' ||
      error?.fields[0]?.code === 'EXTERNAL_IBAN_VALIDATION_FAILED')
  ) {
    // Map every field in the array under its own actual name instead of hardcoding `iban` — IBAN's
    // own case (a single { field: 'iban', ... } entry) renders identically either way; this also
    // lets a pair like GBP's account_number + sort_code attach the same error to both fields.
    fieldsError = {};
    error.fields.forEach(({ field, code }) => {
      fieldsError[field] = resolveFieldErrorMessage(i18n, `error.fieldError.${field}.${code}`);
    });
  } else {
    error?.fields &&
      Object.keys(error.fields).map((field) => {
        if (isRegistration && field === 'password' && error.fields[field] === 'Password') return;
        // eslint-disable-next-line max-len
        fieldsError[field.split('.').pop()] = resolveFieldErrorMessage(
          i18n,
          `error.fieldError.${field}.${error.fields[field]}`,
          {
            variable
          }
        );
      });
  }

  return fieldsError;
};

export const debounce = (func, delay = 400) => {
  let timeout;
  return function (...args) {
    const context = this;
    if (timeout) clearTimeout(timeout);
    timeout = setTimeout(() => {
      timeout = null;
      return func.apply(context, args);
    }, delay);
  };
};

export const MEDIA_QUERY = {
  HEADER_BUTTON_NAMES: '(min-width: 1050px)',
  DESKTOP: '(min-width: 769px)',
  NON_DESKTOP: '(min-width: 320px)',
  MOBILE: '(max-width: 499px)',
  DASHBOARD_RESPONSIVE: '(max-width: 988px)'
};

export const convertDateToServerFilter = (date) => {
  if (date !== null) {
    return `${date.getFullYear()}${date.getMonth() < 9 ? '0' + (date.getMonth() + 1) : date.getMonth() + 1}${
      date.getDate() < 10 ? '0' + date.getDate() : date.getDate()
    }`;
  } else {
    return '';
  }
};

export const convertDateToMessagesFilter = (date) => {
  if (date !== null) {
    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    return `${year}-${month}-${day}`;
  } else {
    return '';
  }
};

export const convertDate = (date, isEndOfDay = false) => {
  if (date !== null) {
    return `${date.getFullYear()}-${date.getMonth() < 9 ? '0' + (date.getMonth() + 1) : date.getMonth() + 1}-${
      date.getDate() < 10 ? '0' + date.getDate() : date.getDate()
    }T${isEndOfDay ? '23:59:59' : '00:00:00'}`;
  }
  return '';
};

export const amountFormattedValue = (value, precision = 2, isWithoutThousandsSeparators = false) => {
  if (value === undefined || value === '' || value === null) {
    // eslint-disable-next-line no-console
    console.error('amountFormattedValue value:', value, typeof value);

    return '';
  }

  const numValue = Number(value);

  if (isNaN(numValue)) {
    // eslint-disable-next-line no-console
    console.error('amountFormattedValue value:', value, typeof value);

    return '';
  }

  let stringValue = numValue.toFixed(precision + 6);

  const dotIndex = stringValue.indexOf('.');
  if (dotIndex !== -1) {
    stringValue = stringValue.slice(0, dotIndex + precision + 1);
  }

  const formattedValue = new Intl.NumberFormat('en-US', {
    maximumFractionDigits: precision,
    minimumFractionDigits: precision,
    useGrouping: !isWithoutThousandsSeparators
  }).format(Number(stringValue));

  const [integerPart, fractionalPart] = formattedValue.split('.');

  if (!fractionalPart || fractionalPart.length < 2) {
    return formattedValue;
  }

  return `${integerPart}.${fractionalPart.replace(/0+$/, '').padEnd(2, '0')}`;
};

export const findAccountByCurrency = ({ accounts, currency, currencyForExclude }) => {
  if (!currency) {
    return accounts.find((account) => account.currency !== currencyForExclude);
  }
  return accounts.reduce((result, account) => {
    if (account.currency === currencyForExclude) {
      return undefined;
    }
    return account.currency === currency;
  }, undefined);
};

// FEES DATA FORMAT FUNCTIONS

export const format = (val) => {
  const formatted = val?.split('_').join(' ').toLowerCase();
  const capitalized = formatted?.charAt(0).toUpperCase() + formatted?.slice(1);
  return capitalized;
};

export const formServiceName = (obj) => {
  return format(obj.name);
};

export const formPaymentMethod = (obj) => {
  if (obj.transfer_type === 'EXCHANGE') {
    return format(obj.transfer_type);
  } else {
    return format(obj.payment_method);
  }
};

export const formTransactionType = (obj) => {
  if (obj.transfer_type === 'RETURN') {
    const capitalizedTransactionType = format(obj.transaction_type);
    const capitalizedTransferType = format(obj.transfer_type);
    return `${capitalizedTransactionType} ${capitalizedTransferType}`;
  } else {
    return format(obj.transaction_type);
  }
};

export function fileExtensionValidation(fileName) {
  const extension = fileName.split('.').pop();
  const allowedExtensions = ['pdf', 'jpg', 'jpeg', 'png'];

  return allowedExtensions.includes(extension);
}

export const trimTextContent = (text) => {
  if (!text) return text; // Return the text as-is if it's empty or null

  text = text.replace(/(\.\s*){2,}/g, '. '); // Remove extra spaces between multiple dots
  text = text.replace(/\s+/g, ' '); // Replace multiple consecutive spaces with a single space

  return text.trim(); // Trim spaces from the beginning and end of the text
};

export const formatAccountNumber = (accountNumber) => {
  if (!accountNumber || accountNumber.length < 12) {
    return accountNumber;
  }

  const firstPart = accountNumber.slice(0, 8);
  const lastPart = accountNumber.slice(-8);

  return `${firstPart}....${lastPart}`;
};

export const isCryptoProvider = (paymentProvider) => {
  return paymentProvider === PAYMENT_PROVIDERS.GRAPHEREX;
};

export const isWallesterProvider = (account) => {
  return account.transfer_provider === PAYMENT_PROVIDERS.WALLESTER;
};

// Convera requires beneficiary state/province for some countries (e.g. US, CA)
export const isBeneficiaryStateProvider = (paymentProvider) => {
  return paymentProvider === PAYMENT_PROVIDERS.CONVERA_API || paymentProvider === PAYMENT_PROVIDERS.CONVERA_MERCHANT;
};

export const isConveraProvider = (paymentProvider) => {
  return (
    paymentProvider === PAYMENT_PROVIDERS.CONVERA ||
    paymentProvider === PAYMENT_PROVIDERS.CONVERA_MERCHANT ||
    paymentProvider === PAYMENT_PROVIDERS.CONVERA_API
  );
};

export const formatDate = (date) => {
  if (!date) return '';
  const dateObj = date instanceof Date ? date : new Date(date);
  const day = String(dateObj.getDate()).padStart(2, '0');
  const month = String(dateObj.getMonth() + 1).padStart(2, '0');
  const year = dateObj.getFullYear();
  return `${day}.${month}.${year}`;
};

export const formatPaymentDate = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  return `${day}.${month}.${year}, ${hours}:${minutes}`;
};

export const formatPaymentDateSeparate = (dateString) => {
  if (!dateString) return { date: '', time: '' };
  const date = new Date(dateString);

  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const day = String(date.getDate()).padStart(2, '0');
  const month = months[date.getMonth()];
  const year = date.getFullYear();
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');

  return {
    date: `${day} ${month} ${year}`,
    time: `${hours}:${minutes}`
  };
};

export const formatExchangeRate = (fromCurrency, toCurrency, rate) => {
  if (!fromCurrency || !toCurrency || !rate) return '';
  return `${fromCurrency} 1 = ${toCurrency} ${rate}`;
};

export const getTransactionTypeIcon = (type) => {
  const iconMap = {
    EXCHANGE: 'new-arrow-convert-icon',
    COMMISSION: 'commission-icon',
    NORMAL: 'payment-icon',
    INTERNAL: 'internal-icon',
    CRYPTO: 'crypto-icon',
    CARD: 'card-icon'
  };
  return iconMap[type] || 'payment-icon';
};

export const isManualTypeProvider = (account) => {
  return (
    account.transfer_provider === PAYMENT_PROVIDERS.MANUAL ||
    account.transfer_provider === PAYMENT_PROVIDERS.H_AND_H ||
    account.transfer_provider === PAYMENT_PROVIDERS.DUKASCOPY ||
    account.transfer_provider === PAYMENT_PROVIDERS.CONVERA ||
    account.transfer_provider === PAYMENT_PROVIDERS.CONVERA_MERCHANT ||
    account.transfer_provider === PAYMENT_PROVIDERS.CONVERA_API
  );
};

/*
 * Sanitizes text from ChatGPT and other external sources
 * Converts problematic Unicode characters to safe alternatives
 * @param {string} text - Input text to sanitize
 * @returns {string} - Sanitized text
 */
export const sanitizeMessageText = (text) => {
  if (!text) return text;

  // Replace common problematic characters with safe alternatives
  const replacements = {
    '\u00A0': ' ', // Non-breaking space -> regular space
    '\u2028': '\n', // Line separator -> newline
    '\u2029': '\n', // Paragraph separator -> newline
    '\u201C': '"', // Left double quotation mark
    '\u201D': '"', // Right double quotation mark
    '\u2018': "'", // Left single quotation mark
    '\u2019': "'", // Right single quotation mark
    '\u2013': '-', // En dash
    '\u2014': '-', // Em dash
    '\u2026': '...', // Horizontal ellipsis
    '\u2022': '*', // Bullet point
    '\u2023': '>', // Triangular bullet
    '\u25E6': 'o', // White bullet
    '\u2043': '-', // Hyphen bullet
    '\u2219': '*' // Bullet operator
  };

  let sanitized = text;
  for (const [char, replacement] of Object.entries(replacements)) {
    sanitized = sanitized.replace(new RegExp(char, 'g'), replacement);
  }

  // Remove zero-width characters
  sanitized = sanitized
    .replace(/\u200B/g, '') // Zero-width space
    .replace(/\uFEFF/g, '') // Zero-width no-break space
    .replace(/\u200C/g, '') // Zero-width non-joiner
    .replace(/\u200D/g, ''); // Zero-width joiner

  // Remove control characters except tab, newline, and carriage return
  // eslint-disable-next-line no-control-regex
  sanitized = sanitized.replace(/[\x00-\x08\x0B\x0C\x0E-\x1F\x7F]/g, '');

  return sanitized;
};

// Mapper for new wallet API response format (camelCase to snake_case)
export const mapWalletResponse = (wallet) => {
  if (!wallet) return null;

  return {
    ...wallet,
    // Map camelCase to snake_case fields
    wallet_number: wallet.walletNumber || wallet.wallet_number,
    multi_wallet_number: wallet.multiCurrencyWalletNumber || wallet.multi_wallet_number,
    multi_iban: wallet.multiCurrencyIban || wallet.multi_iban,
    wallet_account_number: wallet.accountNumber || wallet.wallet_account_number,
    sort_code: wallet.sortCode || wallet.sort_code,
    bank_name: wallet.bankName || wallet.bank_name,
    bank_country: wallet.bankCountry || wallet.bank_country,
    bank_address: wallet.bankAddress || wallet.bank_address,
    account_holder: wallet.bankAccountHolder || wallet.account_holder,
    transfer_provider: wallet.transferProvider || wallet.transfer_provider,
    payment_methods: wallet.paymentMethods || wallet.payment_methods,
    network: wallet.cryptoNetwork || wallet.network,
    network_name: wallet.cryptoNetworkName || wallet.network_name,
    crypto_address: wallet.cryptoAddress || wallet.crypto_address,
    currency_precision: wallet.currencyPrecision || wallet.currency_precision,
    created_at: wallet.createdAt || wallet.created_at,
    wallet_account_holder: wallet.walletAccountHolder || wallet.wallet_account_holder,

    // Handle balance fields - extract amount from objects
    available: wallet.available?.amount ?? wallet.available,
    availableDetails: wallet.available || wallet.availableDetails,
    available_default: wallet.availableDefault?.amount ?? wallet.available_default,
    total: wallet.total?.amount ?? wallet.total,
    totalDetails: wallet.total || wallet.totalDetails,
    on_hold: wallet.onHold?.amount ?? wallet.on_hold,
    onHoldDetails: wallet.onHold || wallet.onHoldDetails
  };
};

// Heuristic check for legacy templates that stored an IBAN in requisites.accountNumber
export const isLikelyIban = (value) => /^[A-Za-z]{2}\d{2}[A-Za-z0-9]{11,30}$/.test((value || '').replace(/\s/g, ''));
